import React, { useEffect, useRef, useState } from "react";
import {
  AssessmentButton,
  AssessmentTemplateIcon,
  Bullets,
} from "ui-components";
import "./TemplateCard.scss";
import { useTranslation } from "react-i18next";

interface TemplateCardProps {
  templateItem?: any;
  title: string;
  totalSkills?: number;
  questionCount: number;
  timeLimit: string;
  addBtnLabel?: string;
  isRemoveVisible?: boolean;
  activeAddBtn?: boolean;
  hideButton?: boolean;
  handleAdd?: (e: any, templateItem: any) => void;
  handleRemove?: (e: any) => void;
  assessmentSkills?: any[];
}

const TemplateCard = ({
  templateItem,
  title,
  totalSkills,
  questionCount,
  timeLimit,
  addBtnLabel,
  isRemoveVisible,
  activeAddBtn,
  hideButton = true,
  handleAdd,
  handleRemove,
  assessmentSkills = [],
}: TemplateCardProps) => {
  const { t } = useTranslation();
  const langSelectRef = useRef<HTMLDivElement>(null);
  const uniqueSkills = Array.from(
    new Map(
      assessmentSkills.map((item: any) => [
        item?.title + "-" + item?.questionType,
        item,
      ]),
    ).values(),
  );
  const [visibleCount, setVisibleCount] = useState(uniqueSkills?.length ?? 0);
  useEffect(() => {
    const calculateVisibleCount = () => {
      const container = langSelectRef.current;
      if (!container) return;
      const containerTop = container.getBoundingClientRect().top;
      const containerHeight = container.offsetHeight;
      const containerBottom = containerTop + containerHeight;
      const children = Array.from(container.children) as HTMLElement[];
      let visible = 0;
      for (const child of children) {
        const childBottom = child.getBoundingClientRect().bottom;
        if (childBottom <= containerBottom) {
          visible++;
        } else {
          break;
        }
      }
      setVisibleCount(visible);
    };
    calculateVisibleCount();
    window.addEventListener("resize", calculateVisibleCount);
    return () => window.removeEventListener("resize", calculateVisibleCount);
  }, [uniqueSkills]);

  useEffect(() => {
    if (assessmentSkills?.length > 0 && visibleCount === 0) {
      setVisibleCount(assessmentSkills?.length);
    }
  }, [assessmentSkills]);
  const hiddenCount = (uniqueSkills?.length ?? 0) - visibleCount;
  return (
    <div className={`showAllTemplate ${isRemoveVisible && "selected"}`}>
      <div className="templateHeading">
        <div
          className={`templateName ${assessmentSkills?.length > 1 && "gap-3"} ${assessmentSkills?.length > 2 && "gap-4"}`}
        >
          <div className="overlappingIcons">
            {assessmentSkills?.length ? (
              assessmentSkills
                ?.slice(0, 3)
                .map(
                  (item, index) =>
                    item?.skillIcon && (
                      <img
                        key={index}
                        src={item?.skillIcon}
                        alt="Skill Icon"
                        className="overlappingIcon"
                      />
                    ),
                )
            ) : (
              <AssessmentTemplateIcon />
            )}
          </div>
          <div>
            <span title={title}>{title}</span>
          </div>
        </div>
      </div>
      <div className="templateDescription">
        <div className="tempDescriptionSec">
          <div className="tempCardSelect">
            <span>{t("GENERAL.TOTAL_SKILLS")}</span>
            <b>{totalSkills}</b>
          </div>
          <div className="tempCardSelect">
            <span>{t("CREATE_ASSESSMENT.NO_OF_QUESTIONS")}</span>
            <b>{questionCount}</b>
          </div>
          <div className="tempCardSelect">
            <span>{t("CREATE_ASSESSMENT.DURATION")}</span>
            <b>{timeLimit}</b>
          </div>
        </div>
        <div className="py-3">
          <hr className="division" />
        </div>
        <div className="skillsSection">
          <div className="langSelect" ref={langSelectRef}>
            {Array.from(
              new Map(
                uniqueSkills
                  ?.slice(0, visibleCount)
                  .map((item: any) => [
                    item?.title + "-" + item?.questionType,
                    item,
                  ]),
              ).values(),
            ).map((item: any, ind: number) => (
              <div key={ind}>
                <Bullets
                  skillName={`${item?.title ?? item?.title} - ${item?.questionType}`}
                />
              </div>
            ))}
          </div>
          {hiddenCount > 0 && <div className="moreSkills">+{hiddenCount}</div>}
        </div>
        {!hideButton && (
          <div className="tempAddBtn">
            {addBtnLabel && (
              <AssessmentButton
                className={activeAddBtn ? "" : "lightBtn"}
                onClick={(e) => handleAdd && handleAdd(e, templateItem)}
              >
                {addBtnLabel}
              </AssessmentButton>
            )}
            {isRemoveVisible && (
              <div className="tempRemoveBtn">
                <span onClick={handleRemove}>{t("GENERAL.REMOVE")}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(TemplateCard);
